import { parsePhoneNumberFromString } from "libphonenumber-js";
import { isWebUrl, normalizeWebUrl } from "@/lib/ads/carousel";

export interface LeadFormFieldOption {
  value: string;
  label: string;
}

export const LEAD_FORM_FIELD_GROUPS: { title: string; fields: LeadFormFieldOption[] }[] = [
  {
    title: "Contact",
    fields: [
      { value: "EMAIL", label: "Email" },
      { value: "PHONE", label: "Phone number" },
      { value: "FULL_NAME", label: "Full name" },
      { value: "FIRST_NAME", label: "First name" },
      { value: "LAST_NAME", label: "Last name" },
      { value: "WHATSAPP_NUMBER", label: "WhatsApp number" },
    ],
  },
  {
    title: "Location",
    fields: [
      { value: "STREET_ADDRESS", label: "Street address" },
      { value: "ADDRESS_LINE_TWO", label: "Address line 2" },
      { value: "CITY", label: "City" },
      { value: "STATE", label: "State" },
      { value: "PROVINCE", label: "Province" },
      { value: "ZIP", label: "Zip code" },
      { value: "POST_CODE", label: "Post code" },
      { value: "COUNTRY", label: "Country" },
    ],
  },
  {
    title: "Demographics",
    fields: [
      { value: "DOB", label: "Date of birth" },
      { value: "GENDER", label: "Gender" },
      { value: "MARITIAL_STATUS", label: "Marital status" },
      { value: "RELATIONSHIP_STATUS", label: "Relationship status" },
      { value: "EDUCATION_LEVEL", label: "Education level" },
      { value: "MILITARY_STATUS", label: "Military status" },
    ],
  },
  {
    title: "Work",
    fields: [
      { value: "COMPANY_NAME", label: "Company name" },
      { value: "JOB_TITLE", label: "Job title" },
      { value: "WORK_EMAIL", label: "Work email" },
      { value: "WORK_PHONE_NUMBER", label: "Work phone number" },
      { value: "WEBSITE", label: "Website" },
    ],
  },
];

export const LEAD_FORM_FIELDS: LeadFormFieldOption[] = LEAD_FORM_FIELD_GROUPS.flatMap((group) => group.fields);

export const NATIONAL_ID_TYPES: { value: string; label: string; country: string }[] = [
  { value: "ID_AR_DNI", label: "Argentina — DNI", country: "Argentina" },
  { value: "ID_CPF", label: "Brazil — CPF", country: "Brazil" },
  { value: "ID_CL_RUT", label: "Chile — RUT", country: "Chile" },
  { value: "ID_CO_CC", label: "Colombia — CC", country: "Colombia" },
  { value: "ID_EC_CI", label: "Ecuador — CI", country: "Ecuador" },
  { value: "ID_PE_DNI", label: "Peru — DNI", country: "Peru" },
  { value: "ID_MX_RFC", label: "Mexico — RFC", country: "Mexico" },
];

export const THANK_YOU_BUTTONS: { value: string; label: string; text: string; target: "url" | "url-required" | "phone" | "file" | "none" }[] = [
  { value: "VIEW_WEBSITE", label: "Visit website", text: "Visit website", target: "url" },
  { value: "BOOK_ON_WEBSITE", label: "Book on website", text: "Book now", target: "url-required" },
  { value: "DOWNLOAD", label: "Download from a link", text: "Download", target: "url-required" },
  { value: "VIEW_ON_FACEBOOK", label: "Download a file (PDF, JPG or PNG)", text: "Download", target: "file" },
  { value: "CALL_BUSINESS", label: "Call business", text: "Call now", target: "phone" },
  { value: "WHATSAPP", label: "Chat on WhatsApp", text: "Chat on WhatsApp", target: "phone" },
  { value: "P2B_MESSENGER", label: "Chat on Messenger", text: "Message us", target: "none" },
  { value: "NONE", label: "No button", text: "Done", target: "url" },
];

export const GATED_FILE_MAX_BYTES = 5 * 1024 * 1024;
export const MAX_OWN_QUESTIONS = 15;

export const ownQuestionCount = (questions: LeadFormQuestion[]) =>
  questions.filter((q) => q.type === "CUSTOM" || q.type === "DATE_TIME").length;

export const LEAD_FORM_LOCALES: { value: string; label: string }[] = [
  { value: "", label: "English (US) — Meta's default" },
  { value: "EN_GB", label: "English (UK)" },
  { value: "HI_IN", label: "Hindi" },
  { value: "AR_AR", label: "Arabic" },
  { value: "CS_CZ", label: "Czech" },
  { value: "DA_DK", label: "Danish" },
  { value: "DE_DE", label: "German" },
  { value: "EL_GR", label: "Greek" },
  { value: "ES_ES", label: "Spanish (Spain)" },
  { value: "ES_LA", label: "Spanish (Latin America)" },
  { value: "FI_FI", label: "Finnish" },
  { value: "FR_FR", label: "French" },
  { value: "HE_IL", label: "Hebrew" },
  { value: "HU_HU", label: "Hungarian" },
  { value: "ID_ID", label: "Indonesian" },
  { value: "IT_IT", label: "Italian" },
  { value: "JA_JP", label: "Japanese" },
  { value: "KO_KR", label: "Korean" },
  { value: "NB_NO", label: "Norwegian" },
  { value: "NL_NL", label: "Dutch" },
  { value: "PL_PL", label: "Polish" },
  { value: "PT_BR", label: "Portuguese (Brazil)" },
  { value: "PT_PT", label: "Portuguese (Portugal)" },
  { value: "RO_RO", label: "Romanian" },
  { value: "RU_RU", label: "Russian" },
  { value: "SV_SE", label: "Swedish" },
  { value: "TH_TH", label: "Thai" },
  { value: "TR_TR", label: "Turkish" },
  { value: "VI_VN", label: "Vietnamese" },
  { value: "ZH_CN", label: "Chinese (Simplified)" },
  { value: "ZH_HK", label: "Chinese (Hong Kong)" },
  { value: "ZH_TW", label: "Chinese (Taiwan)" },
];

export interface LeadFormGatedFile {
  name: string;
  size: number;
  data: string;
}

export const GATED_FILE_ACCEPT = "application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png";

export function gatedFileContentType(bytes: Uint8Array): "application/pdf" | "image/png" | "image/jpeg" | null {
  if (String.fromCharCode(...bytes.slice(0, 5)) === "%PDF-") return "application/pdf";
  if ([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a].every((b, i) => bytes[i] === b)) return "image/png";
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return "image/jpeg";
  return null;
}

export function isGatedFileContent(bytes: Uint8Array): boolean {
  return gatedFileContentType(bytes) !== null;
}

export function readGatedFile(file: File): Promise<LeadFormGatedFile> {
  return new Promise((resolve, reject) => {
    if (!file || file.size === 0) return reject(new Error("This file is empty."));
    if (file.size > GATED_FILE_MAX_BYTES) return reject(new Error("The download file is larger than 5 MB. Choose a smaller file."));
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("This file could not be read. Choose it again."));
    reader.onload = () => {
      const bytes = new Uint8Array(reader.result as ArrayBuffer);
      if (!isGatedFileContent(bytes)) return reject(new Error("The download file must be a PDF, JPG or PNG."));
      let binary = "";
      for (let i = 0; i < bytes.length; i += 0x8000) {
        binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
      }
      resolve({ name: file.name, size: file.size, data: btoa(binary) });
    };
    reader.readAsArrayBuffer(file);
  });
}

export type LeadFormQuestionType = "PREFILL" | "CUSTOM" | "DATE_TIME" | "NATIONAL_ID";

export interface LeadFormQuestion {
  type: LeadFormQuestionType;
  key: string;
  label?: string;
  note?: string;
  options?: Array<{ value: string; key: string }>;
}

export interface LeadFormConsent {
  id: string;
  text: string;
  required: boolean;
  checkedByDefault: boolean;
}

export interface LeadFormDraft {
  name: string;
  questions: LeadFormQuestion[];
  privacy_policy: { url: string; link_text: string };
  context_card: { title: string; text: string; style: "LIST_STYLE" | "PARAGRAPH_STYLE" };
  thank_you_page: { title: string; description: string; button_type: string; button_url: string };
  has_context_card: boolean;
  has_thank_you_customization: boolean;
  higher_intent: boolean;
  sms_verify: boolean;
  questions_headline: string;
  allow_organic: boolean;
  tracking: Array<{ id: string; key: string; value: string }>;
  disclaimer: { enabled: boolean; title: string; body: string; checkboxes: LeadFormConsent[] };
  locale: string;
  gated_file: LeadFormGatedFile | null;
}

export function emptyLeadFormDraft(): LeadFormDraft {
  return {
    name: "",
    questions: [],
    privacy_policy: { url: "", link_text: "Privacy Policy" },
    context_card: { title: "", text: "", style: "LIST_STYLE" },
    thank_you_page: {
      title: "Thanks for signing up!",
      description: "We'll be in touch shortly.",
      button_type: "VIEW_WEBSITE",
      button_url: "",
    },
    has_context_card: false,
    has_thank_you_customization: false,
    higher_intent: false,
    sms_verify: false,
    questions_headline: "",
    allow_organic: false,
    tracking: [],
    disclaimer: { enabled: false, title: "", body: "", checkboxes: [] },
    locale: "",
    gated_file: null,
  };
}

export const thankYouButton = (type: string) => THANK_YOU_BUTTONS.find((b) => b.value === type) || THANK_YOU_BUTTONS[0];

export function phoneForMeta(raw: string): { number: string; country: string | null } | null {
  const digits = String(raw || "").trim().replace(/[\s()-]/g, "");
  if (!digits) return null;
  const parsed = parsePhoneNumberFromString(digits.startsWith("+") ? digits : `+${digits}`);
  if (!parsed || !parsed.isValid()) return null;
  return { number: parsed.number, country: parsed.country || null };
}

export function nationalIdOf(draft: LeadFormDraft) {
  const q = draft.questions.find((row) => row.type === "NATIONAL_ID");
  return q ? NATIONAL_ID_TYPES.find((t) => t.value === q.key) || null : null;
}

export function introContent(card: LeadFormDraft["context_card"]): string[] {
  if (card.style === "PARAGRAPH_STYLE") {
    const paragraph = card.text.trim();
    return paragraph ? [paragraph] : [];
  }
  return card.text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
}

export function leadFormDraftError(draft: LeadFormDraft): string | null {
  if (!draft.name.trim()) return "Give the form a name.";
  if (!draft.questions.length) return "Pick at least one field, such as Email or Full Name.";
  let ids = 0;
  for (const q of draft.questions) {
    if (q.type === "NATIONAL_ID") ids += 1;
    if (q.type === "DATE_TIME" && !String(q.label || "").trim()) return "The appointment question needs its text, such as \"Pick a time\".";
    if (q.type !== "CUSTOM") continue;
    if (!String(q.label || "").trim()) return "Every custom question needs its text.";
    if (q.options) {
      const values = q.options.map((o) => o.value.trim()).filter(Boolean);
      if (values.length < 2) return `"${q.label}" needs at least two answer options.`;
      if (new Set(values.map((v) => v.toLowerCase())).size !== values.length) {
        return `"${q.label}" has the same answer option twice.`;
      }
    }
  }
  if (ids > 1) return "A form can ask for only one national ID. Create a separate form for each country.";
  if (ownQuestionCount(draft.questions) > MAX_OWN_QUESTIONS) {
    return `A form can have at most ${MAX_OWN_QUESTIONS} of your own questions (short answer, multiple choice and appointment).`;
  }
  if (draft.sms_verify && !draft.questions.some((q) => q.type === "PREFILL" && q.key === "PHONE")) {
    return "Phone verification needs the Phone number field in the form.";
  }
  if (!draft.privacy_policy.url.trim()) return "Add your privacy policy link. Meta requires it.";
  if (!isWebUrl(normalizeWebUrl(draft.privacy_policy.url))) {
    return "Enter a privacy policy link like wenext.ai/privacy.";
  }
  if (draft.has_context_card) {
    if (!draft.context_card.title.trim()) return "Add a headline to the intro, or remove the intro.";
    if (!introContent(draft.context_card).length) {
      return draft.context_card.style === "PARAGRAPH_STYLE"
        ? "Add the intro text, or remove the intro."
        : "Add at least one bullet point to the intro, or remove the intro.";
    }
  }
  if (draft.disclaimer.enabled) {
    if (!draft.disclaimer.title.trim()) return "Add a title to the custom disclaimer.";
    if (!draft.disclaimer.body.trim()) return "Add the disclaimer text.";
    const texts = draft.disclaimer.checkboxes.map((c) => c.text.trim().toLowerCase());
    if (texts.some((t) => !t)) return "Every consent checkbox needs its text.";
    if (new Set(texts).size !== texts.length) return "Two consent checkboxes have the same text.";
  }
  const usedKeys = new Set<string>();
  for (const row of draft.tracking) {
    const key = row.key.trim();
    const value = row.value.trim();
    if (!key && !value) continue;
    if (!key || !value) return "Every tracking parameter needs both a name and a value.";
    if (!/^[A-Za-z0-9_.-]+$/.test(key)) return `The tracking parameter name "${key}" can only use letters, numbers, _ . and -.`;
    if (usedKeys.has(key)) return `The tracking parameter "${key}" is listed twice.`;
    usedKeys.add(key);
  }
  if (draft.has_thank_you_customization) {
    const { title, button_type: type, button_url: target } = draft.thank_you_page;
    const button = thankYouButton(type);
    if (!title.trim()) return "Add a headline to the thank-you screen.";
    if (button.target === "file") {
      if (!draft.gated_file?.data) return "Choose the file people download after submitting.";
    } else if (button.target === "phone") {
      const phone = phoneForMeta(target);
      if (!phone) return "Add a valid phone number for the thank-you button, with its country code (for example +91 98765 43210).";
      if (type === "CALL_BUSINESS" && !phone.country) return "Check the country code of the thank-you phone number.";
    } else if (button.target !== "none") {
      if (button.target === "url-required" && !target.trim()) {
        return type === "DOWNLOAD" ? "Add the link people download from." : "Add the booking page link.";
      }
      if (target.trim() && !isWebUrl(normalizeWebUrl(target))) return "Enter a website link for the thank-you button like wenext.ai.";
    }
  }
  return null;
}

export function buildLeadFormPayload(draft: LeadFormDraft): Record<string, unknown> {
  const questions = draft.questions.map((q) => {
    if (q.type === "PREFILL" || q.type === "NATIONAL_ID") return { type: q.key };
    if (q.type === "DATE_TIME") {
      const appointment: Record<string, unknown> = { type: "DATE_TIME", label: String(q.label || "").trim() };
      if (q.note?.trim()) appointment.inline_context = q.note.trim();
      return appointment;
    }
    const custom: Record<string, unknown> = { type: "CUSTOM", label: String(q.label || "").trim() };
    if (q.options) {
      custom.options = q.options.map((o) => ({ value: o.value.trim() })).filter((o) => o.value);
    }
    return custom;
  });

  const payload: Record<string, unknown> = {
    name: draft.name.trim(),
    questions,
    privacy_policy: {
      url: normalizeWebUrl(draft.privacy_policy.url),
      link_text: draft.privacy_policy.link_text.trim() || "Privacy Policy",
    },
  };

  if (draft.has_context_card && draft.context_card.title.trim()) {
    payload.context_card = {
      style: draft.context_card.style === "PARAGRAPH_STYLE" ? "PARAGRAPH_STYLE" : "LIST_STYLE",
      title: draft.context_card.title.trim(),
      content: introContent(draft.context_card),
    };
  }

  if (draft.has_thank_you_customization && draft.thank_you_page.title.trim()) {
    const type = draft.thank_you_page.button_type;
    const button = thankYouButton(type);
    const target = draft.thank_you_page.button_url.trim();
    const phone = button.target === "phone" ? phoneForMeta(target) : null;
    payload.thank_you_page = {
      title: draft.thank_you_page.title.trim(),
      body: draft.thank_you_page.description.trim() || undefined,
      button_type: button.value,
      button_text: button.text,
      ...(button.target === "phone"
        ? { business_phone_number: phone?.number || undefined }
        : button.target === "file" || button.target === "none"
          ? {}
          : { website_url: target ? normalizeWebUrl(target) : undefined }),
    };
    if (button.target === "file" && draft.gated_file) {
      payload.gated_file = { name: draft.gated_file.name, data: draft.gated_file.data };
    }
  }

  if (draft.locale) payload.locale = draft.locale;
  if (draft.higher_intent) payload.is_optimized_for_quality = true;
  if (draft.sms_verify) payload.is_phone_sms_verify_enabled = true;
  if (draft.questions_headline.trim()) payload.question_page_custom_headline = draft.questions_headline.trim();
  payload.block_display_for_non_targeted_viewer = !draft.allow_organic;

  const tracking = Object.fromEntries(
    draft.tracking.map((row) => [row.key.trim(), row.value.trim()] as const).filter(([k, v]) => k && v),
  );
  if (Object.keys(tracking).length) payload.tracking_parameters = tracking;

  if (draft.disclaimer.enabled) {
    payload.custom_disclaimer = {
      title: draft.disclaimer.title.trim(),
      body: { text: draft.disclaimer.body.trim() },
      checkboxes: draft.disclaimer.checkboxes.map((c) => ({
        text: c.text.trim(),
        is_required: c.required,
        is_checked_by_default: c.checkedByDefault,
      })),
    };
  }

  return payload;
}
